import Loading from '@common/components/Loading';
import { bannerFullScreen } from '@common/constants/admob';
import { admobSelector } from '@common/redux/selectors/admob';
import ObjectRender from '@common/screens/RenderScreen/ObjectRender';
import { IRecord } from '@common/types/';
import { IList } from '@common/types/element';
import { getValueBindingComponent } from '@common/utils/component';
import { get, isEmpty, uniqBy } from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  FlatList,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import EmptyList from '../EmptyList';
import Item from './item';
import createStyle from './style';

const CusList: FC<IList> = (attrs) => {
  const {
    children,
    rowMargin,
    admobs,
    dataBinding,
    page,
    totalPage,
    onLoadMore,
    initializeList,
    isLoadMore,
  } = attrs;
  const styles = createStyle(attrs, dataBinding);
  const tableId = get(attrs, 'databaseOptions.tableId');
  const horizontalScroll = get(
    attrs,
    'databaseOptions.options.horizontalScroll'
  );
  const activeSliderAnimation = get(
    attrs,
    'databaseOptions.options.activeSliderAnimation',
    false
  );
  const { width } = Dimensions.get('screen');

  const [contentSizeWidth, setContentSizeWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const flatListRef = useRef<FlatList>(null);

  const textEmpty = useMemo(
    () => getValueBindingComponent(attrs, 'defaultText'),
    []
  );
  const { isUseAdmob } = useSelector(admobSelector);

  const renderTextEmpty = useMemo(() => {
    return (
      <View
        style={[
          styles.cusListWrapper,
          {
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
      >
        <Text style={{ fontSize: 22, fontWeight: '500' }}>{textEmpty}</Text>
      </View>
    );
  }, [textEmpty]);

  // User cannot select table
  if (isEmpty(tableId)) {
    return (
      <View style={styles.emptyListWrapper}>
        <EmptyList attributes={attrs} />
      </View>
    );
  }

  if (initializeList) {
    return <Loading />;
  }

  if (dataBinding.length <= 0) return renderTextEmpty;

  const paginateArr = get(attrs, 'databaseOptions.options.paginate', false);

  if (children.length == 0) return renderTextEmpty;

  const getAdmobComponent = (
    index: number,
    attrs: React.PropsWithChildren<IList>
  ) => {
    if (isUseAdmob && admobs?.length) {
      const admobListComponent = [];
      for (let adIndex = 0; adIndex < admobs.length; adIndex++) {
        if (Platform.OS === 'web' && !admobs[adIndex].placeholder) {
          break;
        }

        if (
          index !== undefined &&
          (index + 1) % admobs[adIndex].perItem === 0
        ) {
          let width: number = 0,
            height: number = 0;
          const isAdFullScreen = bannerFullScreen.includes(
            admobs[adIndex].bannerSize
          );
          if (!isAdFullScreen) {
            const columnCount = attrs.columnCount || 1;
            width = admobs[adIndex].width / columnCount;
            height = (admobs[adIndex].height / admobs[adIndex].width) * width;
          }

          admobListComponent.push(
            <ObjectRender
              keyItem={index}
              arrComp={[
                {
                  ...admobs[adIndex],
                  width,
                  height,
                  hidden: isAdFullScreen,
                },
              ]}
              isScreen={false}
            />
          );
        }
      }

      return (
        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: rowMargin,
          }}
        >
          {admobListComponent}
        </View>
      );
    }
    return null;
  };

  const ListFooterComponent = () => {
    return (
      <TouchableOpacity
        onPress={() => {
          if (totalPage > page) {
            onLoadMore();
          }
        }}
      >
        {isLoadMore ? (
          <Loading />
        ) : (
          <Text style={styles.loadMoreText}>もっとロード</Text>
        )}
      </TouchableOpacity>
    );
  };

  const handleItemPress = (index: number) => {
    if (horizontalScroll && activeSliderAnimation) {
      setActiveIndex(index);
      // Scroll to the item to center it
      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
          viewPosition: 0.5,
        });
      }
    }
  };

  const renderItemList = ({ item, index }: any) => {
    const isHorizontalWithSlider = horizontalScroll && activeSliderAnimation;
    const isActive = isHorizontalWithSlider && index === activeIndex;
    const isHovered = isHorizontalWithSlider && index === hoveredIndex;

    if (!isHorizontalWithSlider) {
      return (
        <View>
          <Item
            key={index}
            item={item}
            attrs={attrs}
            index={index}
            isActive={isActive}
          />
          {getAdmobComponent(index, attrs)}
        </View>
      );
    }

    const itemContent = (
      <Item
        key={index}
        item={item}
        attrs={attrs}
        index={index}
        isActive={isActive}
      />
    );

    const scale = isActive ? 1.15 : 0.9;
    const opacity = isActive ? 1 : isHovered ? 0.8 : 0.6;

    return (
      <View
        style={{
          paddingVertical: 20,
          marginHorizontal: 10,
        }}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => false}
        onResponderGrant={() => {
          setHoveredIndex(index);
        }}
        onResponderRelease={() => {
          setHoveredIndex(null);
          handleItemPress(index);
        }}
        onResponderTerminate={() => {
          setHoveredIndex(null);
        }}
        {...(Platform.OS === 'web' && {
          onMouseEnter: () => {
            setHoveredIndex(index);
          },
          onMouseLeave: () => {
            setHoveredIndex(null);
          },
          onMouseDown: () => {
            handleItemPress(index);
          },
        })}
      >
        <Animated.View
          style={{
            transform: [{ scale }],
            opacity,
          }}
        >
          {itemContent}
        </Animated.View>
      </View>
    );
  };

  const keyExtractor = (item: IRecord) => item?._id || item.id;

  const isShowFooterFLatList = paginateArr && totalPage > page;
  const isHorizontalWithSlider = horizontalScroll && activeSliderAnimation;

  const renderList = () => {
    return (
      <View style={styles.cusListWrapper}>
        {dataBinding?.length ? (
          <FlatList
            ref={flatListRef}
            {...(isShowFooterFLatList && {
              ListFooterComponent: <ListFooterComponent />,
            })}
            horizontal={horizontalScroll}
            {...(!horizontalScroll && { numColumns: attrs.columnCount })}
            key={attrs.columnCount}
            showsHorizontalScrollIndicator={attrs.isWeb ? true : false}
            contentContainerStyle={
              isHorizontalWithSlider
                ? {
                    paddingVertical: 30,
                    paddingHorizontal: 10,
                  }
                : undefined
            }
            data={uniqBy(dataBinding, keyExtractor)}
            showsVerticalScrollIndicator={true}
            keyExtractor={keyExtractor}
            renderItem={renderItemList}
            onEndReachedThreshold={0.1}
            onScrollEndDrag={() => {
              attrs?.setEnableScrollViewScroll &&
                attrs?.setEnableScrollViewScroll(true);
            }}
            onTouchStart={() => {
              if (contentSizeWidth > width) {
                attrs?.setEnableScrollViewScroll &&
                  attrs?.setEnableScrollViewScroll(false);
              }
            }}
            onContentSizeChange={(width, height) => {
              setContentSizeWidth(width);
            }}
            {...(isHorizontalWithSlider && {
              snapToAlignment: 'center',
              snapToInterval: attrs.width + (attrs.rowMargin || 0),
              decelerationRate: 'fast',
              pagingEnabled: false,
            })}
          />
        ) : null}
      </View>
    );
  };

  return <>{renderList()}</>;
};

export default CusList;
